import React, { useState, useEffect, useContext } from 'react';
import { EventInstrument, Instrument } from '../types/Instrument';
import { getAllInstruments } from '../services/instrumentService'; // Import API functions
import { EventContext } from '../context/EventContext';
import { addEventInstrument, getEventInstruments, removeEventInstrument } from '../services/eventInstrumentService';
import { useToast } from '../context/ToastContext';

const EditEventInstrument: React.FC = ()  => {
  const { currentEvent } = useContext(EventContext); // Access currentEvent
  const [availableInstruments, setAvailableInstruments] = useState<Instrument[]>([]);
  const [selectedInstruments, setSelectedInstruments] = useState<EventInstrument[]>([]);
  const [filters, setFilters] = useState({
    isSpecial: false,
    isConcertBand: false,
    isMarchingBand: false,
    isJazzBand: false,
    isOrchestra: false,
    isStrings: false,
    isChoir: false,
  });

  const { addToast } = useToast();

  // Fetch all available instruments and the selected instruments for the current event
  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        const allInstruments = await getAllInstruments();
        setAvailableInstruments(allInstruments);

        if (currentEvent) {
          const eventInstruments = await getEventInstruments(currentEvent.event_id);
          setSelectedInstruments(eventInstruments);
        }
      } catch (error) {
        console.error('Error fetching instruments:', error);
      }
    };

    fetchInstruments();
  }, [currentEvent]);

  // Function to handle filter checkbox changes
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));
  };

  const handleInstrumentChange = async (e: React.ChangeEvent<HTMLInputElement>, instrument: Instrument) => {
    const { checked } = e.target;

    try {
      if (currentEvent) {
        if (checked) {
          // If checked, add the instrument to the event
          await addEventInstrument(currentEvent.event_id, instrument.instrument_id);
          setSelectedInstruments((prevSelected) => [
            ...prevSelected,
            { instrument_id: instrument.instrument_id, event_id: currentEvent.event_id } as EventInstrument,
          ]);
          addToast(`${instrument.instrument_name} added.`)
        } else {
          // If unchecked, remove the instrument from the event
          await removeEventInstrument(currentEvent.event_id, instrument.instrument_id);
          setSelectedInstruments((prevSelected) =>
            prevSelected.filter((selected) => selected.instrument_id !== instrument.instrument_id)
          );
          addToast(`${instrument.instrument_name} removed.`)
        }
      }
    } catch (error) {
      addToast('Error updating instrument', true);
      console.error('Error updating event instruments:', error);
    }
  };

  // Filter available instruments based on the selected filters
  const filterInstruments = (instrument: Instrument) => {
    if (!filters.isSpecial && instrument.is_special) return false;

    const selectedCategories = [
      filters.isConcertBand,
      filters.isMarchingBand,
      filters.isJazzBand,
      filters.isOrchestra,
      filters.isStrings,
      filters.isChoir,
    ];

    if (selectedCategories.every((category) => !category)) return true;

    return (
      (filters.isConcertBand && instrument.is_concert_band) ||
      (filters.isMarchingBand && instrument.is_marching_band) ||
      (filters.isJazzBand && instrument.is_jazz_band) ||
      (filters.isOrchestra && instrument.is_orchestra) ||
      (filters.isStrings && instrument.is_strings) ||
      (filters.isChoir && instrument.is_choir)
    );
  };

  // Check if an instrument is selected for the current event
  const isSelected = (instrument: Instrument) =>
    selectedInstruments.some((selected) => selected.instrument_id === instrument.instrument_id);

  // Render instruments grouped by type
  const groupedInstruments = availableInstruments.reduce((groups, instrument) => {
    const { instrument_type_name } = instrument;
    if (!groups[instrument_type_name]) groups[instrument_type_name] = [];
    groups[instrument_type_name].push(instrument);
    return groups;
  }, {} as Record<string, Instrument[]>);

  const filteredInstruments = availableInstruments.filter(filterInstruments);

  return (
    <div>
      <h1>Edit Instruments for Event</h1>
      <p>Select or deselect instruments for this event:</p>

      {/* Filter checkboxes */}
      <div>
        <h3>Filter by type</h3>
        <label>
          <input
            type="checkbox"
            name="isSpecial"
            checked={filters.isSpecial}
            onChange={handleFilterChange}
          />
          Show Special
        </label>
        <label>
          <input
            type="checkbox"
            name="isConcertBand"
            checked={filters.isConcertBand}
            onChange={handleFilterChange}
          />
          Concert Band
        </label>
        <label>
          <input
            type="checkbox"
            name="isMarchingBand"
            checked={filters.isMarchingBand}
            onChange={handleFilterChange}
          />
          Marching Band
        </label>
        <label>
          <input
            type="checkbox"
            name="isJazzBand"
            checked={filters.isJazzBand}
            onChange={handleFilterChange}
          />
          Jazz Band
        </label>
        <label>
          <input
            type="checkbox"
            name="isOrchestra"
            checked={filters.isOrchestra}
            onChange={handleFilterChange}
          />
          Orchestra
        </label>
        <label>
          <input
            type="checkbox"
            name="isStrings"
            checked={filters.isStrings}
            onChange={handleFilterChange}
          />
          Strings
        </label>
        <label>
          <input
            type="checkbox"
            name="isChoir"
            checked={filters.isChoir}
            onChange={handleFilterChange}
          />
          Choir
        </label>
      </div>

      {/* Render instruments, separating selected and available */}
      {Object.entries(groupedInstruments).map(([typeName, instrumentGroup]) => (
        <div key={typeName} style={{ marginBottom: '20px' }}>
          <div className='card'>
          <h2>{typeName}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {instrumentGroup
              .filter((instrument) => filteredInstruments.includes(instrument))
              .map((instrument) => (
                <div key={instrument.instrument_id} style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id={`instrument-${instrument.instrument_id}`}
                    name="instrument_ids"
                    value={instrument.instrument_id}
                    checked={isSelected(instrument)}
                    onChange={(e) => handleInstrumentChange(e, instrument)}
                    disabled={currentEvent?.is_locked}
                  />
                  <label htmlFor={`instrument-${instrument.instrument_id}`} style={{ marginLeft: '8px' }}>
                    {instrument.instrument_name} {isSelected(instrument) ? '(Selected)' : '(Available)'}
                  </label>
                </div>
              ))}
          </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EditEventInstrument;
